import React, { memo, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Typography } from 'antd'
import { filter, isEmpty, isNil, map, pick } from 'lodash'
import PropTypes from 'prop-types'

import { findNodeByValue } from '../../../common/utils'

import { useIngredientsExplanationsLazyQuery } from '../../hooks'
import { selectDishById } from '../../redux/selectors'
import { StyledDishDetailsContainer } from '../styled'

import DishDetailsBlacklistedIngredients from './dish-details-blacklisted-ingredients'
import DishDetailsIngredientExplanations from './dish-details-ingredient-explanations'
import DishDetailsIngredientOverrides from './dish-details-ingredient-overrides'
import DishDetailsIngredientReplacements from './dish-details-ingredient-replacements'
import DishDetailsTimestamps from './dish-details-timestamps'
import DishDetailsIngredientFeatures from './dish-details-used-ingredient-features'

const DishDetailsDrawerContent = ({ dishId }) => {
  const { ingredientOptions } = useSelector(store => store.common.metadata.data)

  const dish = useSelector(state => selectDishById(state, dishId))

  const {
    error,
    data: ingredientExplanations,
    isLoading,
    fetch: fetchExplanations,
  } = useIngredientsExplanationsLazyQuery(dishId)

  const {
    mainIngredients = [],
    additionalIngredients = [],
    choiceIngredients = [],
    blacklistedIngredients = [],
  } = dish || {}

  const dishIngredients = useMemo(
    () => [...mainIngredients, ...additionalIngredients, ...choiceIngredients],
    [additionalIngredients, mainIngredients, choiceIngredients],
  )

  const timestamps = useMemo(
    () =>
      pick(dish, [
        'tierOneDoneAt',
        'tierOneDoneBy',
        'curatedAt',
        'curatedBy',
        'qadAt',
        'qadBy',
        'qcdAt',
        'qcdBy',
        'autoeatUpdatedAt',
        'lastModifiedByTierOneAt',
        'lastModifiedByCuratorAt',
        'lastModifiedByQaAt',
        'lastModifiedByQcAt',
        'lastRestSuggestionsAt',
        'lastPubSubSuggestionsAt',
        'lastModifiedByTierOne',
        'lastModifiedByCurator',
        'lastModifiedByQa',
        'lastModifiedByQc',
      ]),
    [dish],
  )

  const ingredientOverrides = useMemo(
    () => [
      ...map(
        filter(dishIngredients, ({ overrides }) => !isEmpty(overrides)),
        ({ ingredient, overrides }) => ({
          namePath: findNodeByValue(ingredientOptions, ingredient?.id)
            ?.namePath,
          overriden: overrides,
        }),
      ),
    ],
    [dishIngredients, ingredientOptions],
  )

  const ingredientReplacements = useMemo(
    () =>
      map(
        filter(
          dishIngredients,
          ({ replacesIngredientId }) => !isEmpty(replacesIngredientId),
        ),
        ({ ingredient, replacesIngredientId }) => ({
          replaces: findNodeByValue(ingredientOptions, replacesIngredientId)
            ?.namePath,
          replacement: findNodeByValue(ingredientOptions, ingredient?.id)
            ?.namePath,
        }),
      ),
    [dishIngredients, ingredientOptions],
  )

  const blacklistedIngredientsWithNamePath = useMemo(
    () =>
      map(blacklistedIngredients, blacklistedIngredient => ({
        ...blacklistedIngredient,
        namePath: findNodeByValue(
          ingredientOptions,
          blacklistedIngredient.ingredient?.id,
        )?.namePath,
      })),
    [blacklistedIngredients, ingredientOptions],
  )

  useEffect(() => {
    if (!isNil(dishId)) {
      fetchExplanations()
    }
  }, [dishId, fetchExplanations])

  if (isLoading) {
    return null
  }

  if (error) {
    return <Typography.Paragraph>{error}</Typography.Paragraph>
  }

  return (
    <StyledDishDetailsContainer>
      <DishDetailsIngredientExplanations
        ingredientExplanations={ingredientExplanations}
        dishIngredients={dishIngredients}
        ingredientsTreeData={ingredientOptions}
      />
      <DishDetailsIngredientOverrides
        ingredientOverrides={ingredientOverrides}
      />
      <DishDetailsIngredientReplacements
        ingredientReplacements={ingredientReplacements}
      />
      <DishDetailsIngredientFeatures dishIngredients={dishIngredients} />
      <DishDetailsBlacklistedIngredients
        blacklistedIngredients={blacklistedIngredientsWithNamePath}
      />
      <DishDetailsTimestamps timestamps={timestamps} />
    </StyledDishDetailsContainer>
  )
}

DishDetailsDrawerContent.propTypes = {
  dishId: PropTypes.string.isRequired,
}

export default memo(DishDetailsDrawerContent)
